import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ProjectHomeNav,
  PROJECT_NAV_DEFAULT_EXPANDED,
  type ProjectNavExpandKey,
} from './ProjectHomeNav';
import { projectNavTarget, projectSpaceTarget } from './project-nav-targets';
import { listTeams, type TeamDto } from '../../api/project-teams';
import { ProjectTeamsContext } from './project-teams-context';
import { tProject } from '../../lib/project-i18n';
import './ProjectPage.css';

/**
 * ⭐ **프로젝트 2차 네비를 «홈 밖의 화면»에도 두는 껍데기** (2026-09-30).
 *
 * ## 무엇이 문제였나
 *
 * 네비(`ProjectHomeNav` — 이 레포가 「프로젝트 메뉴」라고 부르는 것,
 * `docs/design/adr-20260812-reservation-domain.md:66`)를 그리는 곳이 **`ProjectPage` 하나뿐**이었다.
 * ```
 *   /project            ⇒ 메뉴 «있음»
 *   /project/teams · /project/projects · /project/my-tasks  ⇒ ⛔ 메뉴 «없음»
 *     ⛔ 2026-10-12 — 종전 이 줄 끝에 `/project/stats` 가 있었다. 사용자 결정으로 걷혔다.
 *       ⚠ 그래서 아래 「네 화면」의 «수는 그대로»다 — 통계가 빠진 자리에
 *         `/project/search`(2026-10-07)가 들어와 teams·projects·my-tasks·search 넷이다.
 * ```
 * ⇒ [내 할일] 을 누르는 순간 그 메뉴가 사라진다 — **이동 수단을 밟고 들어갔더니 이동 수단이 없다.**
 *   다시 건너가려면 뒤로 가야 했다(사용자 지시 2026-09-16: 「내 할일에도 프로젝트 메뉴 나오게」).
 *
 * ## ⚠ 감싸는 쪽은 «자기 화면을 안 고친다»
 *
 * 라우터에서 감싸기만 한다. 네 화면의 DOM·CSS 는 한 줄도 안 바뀐다(레포 규칙 — 회귀 금지).
 * ```
 *   { path: 'project/my-tasks', element: <ProjectNavShell><MyTasksDetailPage /></ProjectNavShell> }
 * ```
 *
 * ## ⛔ 옛 가지를 «그대로 베끼지 않았다»
 *
 * `feature/dev-syk` 의 같은 이름 파일은 **main 에 없는 곳**으로 간다:
 * ```
 *   /project/space/:teamId   ⇒ (2026-09-30 당시) main 에 그 라우트가 «없다»   (죽는 이동)
 *     ⚠ 2026-10-08 갱신 — ★ 그 주소는 ⭐ «생겼다»(2차 1차가 뚫었다). ⇒ ⛔ 다만 ★ 깃발
 *       `PROJECT_V2` 뒤이고 ⇒ ⭐ 꺼지면 ★ 홈으로 되돌린다. ⇒ ⇒ 그래서 ★ «셸이 거기로 보내는
 *       것»은 여전히 ⛔ 안 된다 — ★ 꺼진 동안 ⭐ 되돌려지는 자리로 보내는 꼴이 된다.
 *   /project?view=projects   ⇒ main 의 ProjectPage 는 ?view= 를 «안 읽는다» (죽는 이동)
 *     ⇒ ⭐ 2026-10-08 다시 쟀다: ★ 여전히 참이다(`project-nav-targets.test.ts` 가 단언한다).
 * ```
 * ⇒ ★ 목적지는 `project-nav-targets.ts` 한 곳에서 오고, 그 값은 **main 의 ProjectPage 가 쓰던 것**이다.
 *
 * ## ⭐⭐ 2026-10-08 — ★ 「정본으로 되돌릴까」를 ⭐ 다시 쟀다 ⇒ ⛔ **되돌릴 것이 없다**
 *
 * 2차(프로젝트 메뉴 v2)를 지으며 ★ 「이 셸을 ⭐ 정본 것으로 바꾼다」가 ★ 한 판으로 올라왔다.
 * ⇒ 그래서 ★ 두-점으로 다시 쟀고 ⇒ ⭐ **접었다**. 그 수를 여기 남긴다 — ⚠ 까닭:
 *   ⇒ ★ 다음 사람이 ⭐ «같은 질문»을 할 자리가 ★ 바로 여기다(내가 여기서 답을 찾았다).
 * ```
 * ① 날짜    이 파일 ★ 2026-09-30   ·   정본 ★ 2026-09-16      ⇒ ⭐ main 이 «2주 새것»
 * ② 항목    정본  teams · projects · my-tasks · favorites · trash
 *           main  home · teams · projects · my-tasks · favorites
 *           ⇒ ⛔ 정본엔 ★ 「홈」이 «없다» — ⭐ 그것은 ★ 2026-10-07 «사용자 결정»이다
 *             (project-nav-targets.ts 의 `key === 'home'` · PROJECT_NAV_KEYS)
 *           ⇒ ⇒ ★ 즉 ⭐ 정본이 그 결정보다 «오래된» 것이다
 * ③ 목적지  정본 :48 `navigate('/project?view=projects')`  ⇐ ⛔ 죽는 이동(위)
 *           main      `navigate(projectNavTarget(key))`     ⇐ ⭐ «한 곳»에서 온다
 *           ⇒ ⛔ 정본으로 바꾸면 ★ 목적지가 ⭐ 네 곳으로 흩어지고 ★ 하나는 죽는다
 * ④ 휴지통  정본의 다섯째 항목(2026-09-07 사용자 지시 — 즐겨찾기 아래)은 ★ «펼치면 서버를 부른다»
 *           main 프론트  listTrashedProjects ★ 0 · restoreProject ★ 0 · TrashedProjectDto ★ 0
 *           main 서버    trash ★ 0파일 · restore ★ 0파일
 *             [대조군] 같은 폴더에서 `project` ⇒ ★ 30파일  ⇒ ⭐ 훑기가 죽어 0 인 것이 아니다
 *           ⇒ ⇒ ⛔ 지금은 ★ 세울 수 «없다». ⇒ ⚠ 그 결정은 ★ «사라지지 않게» 관제가 올렸다.
 * ```
 * ⇒ ⭐⭐ **결론**: 정본엔 ★ 「홈」이 없고(사용자 결정) ⇒ ★ 「휴지통」은 끝점이 0 이며
 *   ⇒ ★ 목적지 한 곳 모으기(이 파일이 쥔 것)가 ⭐ 흩어진다. ⇒ ⇒ ⛔ **바꿀 것이 남지 않는다.**
 * ⚠⚠ 2026-10-09 — ★ 아래 줄이 ⭐ «코드와 달랐다». 그 이력을 남긴다:
 *   ⇒ ★ 「`active`·`flush`·`children` 뿐이라」고 적혀 있었는데 ⇒ ⛔ 서명에는 ★ `active` 가 ⭐ «없었다».
 *   ⇒ ⇒ ⚠ 4-5 를 재던 피어가 ★ 그 글을 보고 ⇒ ⭐ 「위험이 풀렸다」로 적었고 ⇒ 서명을 열어 ★ 뒤집었다.
 *     ⇒ ⇒ ⛔ 「없는데 ★ 있다고 적힌」 주석은 ⇒ ⭐ 「안 해도 된다」로 읽혀 ★ 일을 빠뜨린다.
 *   ⇒ ✅ 2026-10-09 (4-5)에 ★ `active` 를 ⭐ «선택 인자»로 더해 ⇒ ⇒ ★ 그 글이 ⭐ 참이 되었다.
 *
 * ⇒ ✅ 그리고 ★ V2 화면들은 ⭐ 이 셸을 «그대로 쓴다» — ★ `active`·`flush`·`children` 뿐이라
 *   ⇒ ⭐ 라우터에서 감싸면 된다(2-1′·2-2′ 가 그렇게 했다). ⇒ ⛔ 「바꿀 까닭」이 거기에도 없다.
 * ⇒ ⚠⚠ 그러므로 ★ **2차 메뉴(셸·항목·목적지)는 ⭐ «깃발 이관 대상이 아니다»** —
 *   ⇒ ★ 깃발이 가리는 것은 ⭐ «화면들»이다(관제 2026-10-08).
 *
 * ⚠ 그리고 ★ 이 주석이 ⭐ «다음에 세는 사람의 수를 늘린다»:
 *   ⇒ ★ 위 글에 `/project/space/`·`?view=`·`trash` 같은 글자가 ⭐ 들어 있다.
 *   ⇒ ⇒ ⭐ 그러니 ★ 그 낱말을 셀 때는 ⇒ ⭐ **주석을 «길이 보존»으로 덮고 세라**
 *     (이 레포의 가드들이 ★ 그렇게 한다 — `maskComments`).
 *
 * ## ⚠ `flush` — 「여백·스크롤을 두 겹으로 주지 않는다」
 *
 * 이 껍데기의 `<main>` 은 여백과 스크롤을 준다. 그런데 **스스로 높이를 꽉 채우고 스크롤도 직접
 * 다루는 화면**(`.pj-page--shell` 을 쓰는 `TeamsDetailPage`)을 그대로 넣으면
 * 안쪽 `height:100%` 가 바깥 아래 여백(24px)만큼 **잘린다**. 그때 `flush` 를 켠다.
 */
export function ProjectNavShell({
  active,
  flush = false,
  children,
}: {
  /**
   * ⭐ 지금 보고 있는 화면 — ⇒ 그 항목이 ★ «펼쳐진 채» 시작한다.
   *
   * ⚠⚠ 2026-10-09 (2차 4-5) — ★ `active` 를 ⭐ «선택 인자»로 더했다.
   *   ⇒ ★ 안 주면 ⭐ 여전히 `PROJECT_NAV_DEFAULT_EXPANDED` ★ 한 곳에서 온다(⇒ 아래 주석 그대로).
   *   ⇒ ⇒ ★ 주는 쪽은 ⭐ 「★ 내가 어디 있나」를 보여야 하는 화면뿐이다(⇒ 4-5 보드).
   * ⇒ ⚠ 그리고 ★ `null` 은 ⭐ «해당 항목이 없다»는 뜻이다 — ⇒ ⛔ 「안 줬다」와 ★ 다르다.
   *   ⇒ ⇒ ⭐ 그래서 아래에서 ★ `active !== undefined` 로 가른다 — ⛔ `active ??` 가 아니다.
   *     ⇒ ⇒ ★ 같은 가름을 ⭐ `ProjectV2Route` 의 `fallback` 에서도 썼다(#2310).
   */
  active?: ProjectNavExpandKey | null;
  /** 컨텐츠가 높이를 꽉 채우고 스크롤도 직접 다루면 true — 여백·스크롤을 여기서 안 준다. */
  flush?: boolean;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<TeamDto[]>([]);
  /*
   * ⭐ 「하위 메뉴가 기본 오픈」 — 값은 `ProjectHomeNav` 한 곳에서 온다.
   *   ⛔ 여기에 'teams' 라고 다시 적지 않는다. 적으면 홈과 갈라진다.
   * ⇒ ⭐ 2026-10-09 — ★ 부르는 쪽이 `active` 를 주면 ⇒ ⭐ 그것이 «이 화면의» 시작값이다.
   *   ⇒ ⇒ ★ 안 주면(= undefined) ⇒ ⭐ 위 한 곳에서 온다 ⇒ ⇒ ★ 기존 호출자 셋은 ⭐ 무변경이다.
   */
  const [expanded, setExpanded] = useState<ProjectNavExpandKey | null>(
    active !== undefined ? active : PROJECT_NAV_DEFAULT_EXPANDED,
  );
  const [collapsed, setCollapsed] = useState(false);

  // 실패해도 네비는 살아 있게 — 팀 목록만 빈다(홈의 3c 위젯과 같은 정책).
  /*
   * ⭐ 2026-10-12 — ★ 이 읽기를 ⭐ «다시 부를 수 있게» 꺼냈다.
   *
   * ```
   * 종전 ⇒ useEffect 안에 인라인 ⇒ ⇒ ⛔ 마운트 때 «한 번»만 돌았다
   *   ⇒ ⇒ 그래서 본문에서 팀을 지워도 ★ 서브메뉴에 남았다(사용자 지적)
   * ★ 지금 ⇒ ⭐ `reloadTeams` 를 ⇒ ★ context 로 아래에 내려 ⇒ 본문이 부른다
   * ⇒ ✅ 회귀 0 ⇒ ⇒ 마운트 때 ⭐ 한 번 도는 것은 ★ 그대로다(아래 useEffect)
   * ```
   */
  const reloadTeams = useCallback(() => {
    void listTeams().then(setTeams).catch(() => setTeams([]));
  }, []);

  useEffect(() => {
    reloadTeams();
  }, []);

  const openNav = useCallback((key: Parameters<typeof projectNavTarget>[0]) => {
    navigate(projectNavTarget(key));
  }, [navigate]);

  return (
    <div className={`pj-page pj-page--shell pj-home${collapsed ? ' pj-home--navoff' : ''}`}>
      <ProjectHomeNav
        expanded={expanded}
        onToggle={(k) => setExpanded((prev) => (prev === k ? null : k))}
        teams={teams}
        onOpenNav={openNav}
        onOpenTeam={(teamId) => navigate(projectSpaceTarget(teamId))}
        onCollapse={() => setCollapsed(true)}
      />

      <main className={`pj-home-content${flush ? ' pj-home-content--flush' : ''} scrollbar-thin`}>
        {collapsed && (
          <button
            type="button"
            className="pj-teams-side-new pj-home-navexpand"
            onClick={() => setCollapsed(false)}
            aria-label={tProject('project.home.nav_collapse')}
            title={tProject('project.home.nav_collapse')}
          >
            »
          </button>
        )}
        <ProjectTeamsContext.Provider value={{ teamsChanged: reloadTeams }}>
          {children}
        </ProjectTeamsContext.Provider>
      </main>
    </div>
  );
}
